// https://stackoverflow.com/questions/27078285/simple-throttle-in-js
export function throttle(wait, func, options) {
    var context, args, result;
    var timeout = null;
    var previous = 0;
    if (!options) options = {};
    var later = function () {
        previous = options.leading === false ? 0 : Date.now();
        timeout = null;
        result = func.apply(context, args);
        if (!timeout) context = args = null;
    };
    return function () {
        var now = Date.now();
        if (!previous && options.leading === false) previous = now;
        var remaining = wait - (now - previous);
        context = this;
        args = arguments;
        if (remaining <= 0 || remaining > wait) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
            }
            previous = now;
            result = func.apply(context, args);
            if (!timeout) context = args = null;
        } else if (!timeout && options.trailing !== false) {
            timeout = setTimeout(later, remaining);
        }
        return result;
    };
}

export function debounce(func, wait, immediate) {
    var timeout;
    return function () {
        var context = this, args = arguments;
        var later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
}

export function isInViewport(el) {
    var rect = el.getBoundingClientRect();
    return rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
}

export function isEscape(e) {
    // keyCode is getting deprecated use key instead when supported, which makes this messy
    var evt = e || window.event;
    var isEscape = false;
    if ("key" in evt) {
        isEscape = (evt.key == "Escape" || evt.key == "Esc");
    } else {
        isEscape = (evt.keyCode == 27);
    }
    return isEscape;
}

export function trapFocus(trap, options = {}) {
    const { 
        focusFirst = true, 
        focusDelay = 350,
        onEscape = null,
        onExitTop = null,
        onExitBottom = null 
    } = options;

    const focusableSelector = 'button:not([tabindex="-1"]):not([disabled]), [href]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe';
    const focusableContent = trap.querySelectorAll(focusableSelector);
    const focusableElements = Array.from(focusableContent);
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    function handleKeydown(e) {
        // Handle Escape
        if (e.key === 'Escape' || e.keyCode === 27) {
            if (onEscape) {
                e.preventDefault();
                onEscape();
            }
            return;
        }

        // Handle Tab
        const isTabPressed = e.key === 'Tab' || e.keyCode === 9;
        if (!isTabPressed) return;

        if (e.shiftKey) {
            // Shift + Tab: going backwards
            if (document.activeElement === firstFocusableElement) {
                e.preventDefault();
                if (onExitTop) {
                    onExitTop();
                } else {
                    lastFocusableElement.focus();
                }
            }
        } else {
            // Tab: going forwards
            if (document.activeElement === lastFocusableElement) {
                e.preventDefault();
                if (onExitBottom) {
                    onExitBottom();
                } else {
                    firstFocusableElement.focus();
                }
            }
        }
    }

    function handleArrowKeys(e) {
        const currentIndex = focusableElements.indexOf(document.activeElement);
        if (currentIndex === -1) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (currentIndex < focusableElements.length - 1) {
                    focusableElements[currentIndex + 1].focus();
                } else {
                    firstFocusableElement?.focus();
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (currentIndex > 0) {
                    focusableElements[currentIndex - 1].focus();
                } else if (onExitTop) {
                    onExitTop();
                } else {
                    lastFocusableElement?.focus();
                }
                break;
            case 'Home':
                e.preventDefault();
                firstFocusableElement?.focus();
                break;
            case 'End':
                e.preventDefault();
                lastFocusableElement?.focus();
                break;
        }
    }

    // Add event listeners
    trap.addEventListener('keydown', handleKeydown);
    trap.addEventListener('keydown', handleArrowKeys);

    // Focus first element if requested
    if (focusFirst && firstFocusableElement) {
        setTimeout(() => {
            firstFocusableElement.focus();
        }, focusDelay);
    }

    // Return cleanup function to remove event listeners
    return function releaseFocus() {
        trap.removeEventListener('keydown', handleKeydown);
        trap.removeEventListener('keydown', handleArrowKeys);
    };
}

export function returnFocus() {
    if (typeof window.returnFocusTo !== 'undefined') {
        window.returnFocusTo.focus();
    }
}
